/**
 * date-utils.ts
 *
 * Utility per la gestione delle date, in particolare per evitare
 * il bug di conversione timezone con Date.toISOString().
 */

import dayjs from 'dayjs';

/**
 * Converte un Date in stringa ISO preservando la data locale (senza conversione UTC).
 * Evita il bug per cui toISOString() sposta la data indietro di un giorno in timezone CET.
 */
export function toDateOnlyISOString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00`;
}

/**
 * Converte un Date in stringa 'YYYY-MM-DD' in ora locale (senza componente ora).
 * Da usare per i filtri periodo inviati al backend (KanbanBoardFilters.from/to).
 */
export function toDateOnlyString(d: Date): string {
  return dayjs(d).format('YYYY-MM-DD');
}

/**
 * Interpreta una stringa 'YYYY-MM-DD' come data locale, evitando il bug per cui
 * `new Date('2026-09-01')` la interpreta come UTC mezzanotte (spostandola indietro
 * di un giorno nei fusi orari a est di UTC, come CET).
 */
export function parseDateOnly(s?: string | null): Date | null {
  return s ? dayjs(s, 'YYYY-MM-DD').toDate() : null;
}

/**
 * Differenza in giorni interi tra due date (a - b), troncando all'inizio giornata.
 * Restituisce null se una delle due date è mancante o non valida.
 */
export function diffInDays(a?: string | Date | null, b?: string | Date | null): number | null {
  if (a === undefined || a === null || b === undefined || b === null) return null;
  const dayA = dayjs(a);
  const dayB = dayjs(b);
  if (!dayA.isValid() || !dayB.isValid()) return null;
  return dayA.startOf('day').diff(dayB.startOf('day'), 'day');
}
